/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Confirms approved/rejected appointments via API for ViewAppointments bulk confirm flow.
 * @module logic/appointments/confirmApprovedAppointments
 */

import { logger, sanitizeError } from "../../utils/logger.js";
import { updateAppointment } from "../appointment/index.js";

/**
 * Filters appointments eligible for bulk confirmation (approved or rejected, not yet confirmed).
 * @param {Array<Object>} appointments
 * @returns {Array<Object>}
 */
export const getAppointmentsToConfirm = (appointments) =>
  appointments.filter(
    (appointment) =>
      appointment.estado === "approved" || appointment.estado === "rejected",
  );

/**
 * Marks each eligible appointment as confirmed through the API.
 * @param {Object} options
 * @param {import("react-router").NavigateFunction} options.navigate
 * @param {string} options.eventId
 * @param {Array<Object>} options.appointments
 * @returns {Promise<Set<string>>} Confirmed appointment ids
 */
export const confirmApprovedAndRejectedAppointments = async ({
  navigate,
  eventId,
  appointments,
}) => {
  const appointmentsToConfirm = getAppointmentsToConfirm(appointments);

  if (appointmentsToConfirm.length === 0) {
    return new Set();
  }

  await Promise.all(
    appointmentsToConfirm.map(async (appointment) => {
      try {
        await updateAppointment(navigate, appointment._id, eventId, {
          appointmentInformation: { confirmado: true },
        });
      } catch (error) {
        logger.error(`Error updating appointment ${appointment._id}:`, {
          err: sanitizeError(error),
        });
      }
    }),
  );

  return new Set(appointmentsToConfirm.map((appointment) => appointment._id));
};
